import { useState, useRef, useEffect, useCallback } from "react";
import { Smile, Send, Paperclip, Loader2, UploadCloud, X } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import { chatBulkUploadApi } from "../../app/api";
import toast from "react-hot-toast";
import imageCompression from "browser-image-compression";
import { convertImagesToWebP } from "../../utils/convertImageToWebP";
 
export default function ChatInput({ onSend, onTyping, members = [] }) {
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showFileConfirm, setShowFileConfirm] = useState(false);
 
  // Compression Modal State
  const [showCompressionModal, setShowCompressionModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
 
  // Tagging State
  const [showTagList, setShowTagList] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const [cursorPos, setCursorPos] = useState(0);
 
  const fileInputRef = useRef(null);
  const emojiRef = useRef(null);
  const inputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const typingTimerRef = useRef(null);

  const getMemberName = (member) =>
    member?.name || [member?.first_name, member?.last_name].filter(Boolean).join(" ") || "User";
 
  const handleSend = () => {
    if (!text.trim()) return;
 
    onSend(text);
    setText("");
    setShowEmoji(false);
    setShowTagList(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  };
 
  const handleChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    setText(val);
    setCursorPos(pos);
    if (val.trim()) {
      window.clearTimeout(typingTimerRef.current);
      typingTimerRef.current = window.setTimeout(() => {
        onTyping?.();
      }, 120);
    }
 
    // Detect @ mention
    const textBeforeCursor = val.slice(0, pos);
    const words = textBeforeCursor.split(/\s/);
    const lastWord = words[words.length - 1];
 
    if (lastWord.startsWith("@")) {
      setShowTagList(true);
      setTagSearch(lastWord.slice(1).toLowerCase());
    } else {
      setShowTagList(false);
    }
  };

  useEffect(() => () => window.clearTimeout(typingTimerRef.current), []);
 
  const handleSelectTag = (member) => {
    const textBeforeCursor = text.slice(0, cursorPos);
    const textAfterCursor = text.slice(cursorPos);
   
    const words = textBeforeCursor.split(/\s/);
    words[words.length - 1] = `@${getMemberName(member)} `;
   
    const newText = words.join(" ") + textAfterCursor;
    setText(newText);
    setShowTagList(false);
    inputRef.current?.focus();
  };
 
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
 
  const formatFileSize = (bytes = 0) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFiles = useCallback((files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files).filter(Boolean);
   
    if (fileList.length > 10) {
      toast.error("You can only upload up to 10 files at once.");
      return;
    }
 
    const MAX_SIZE = 50 * 1024 * 1024; // 50MB
 
    const oversized = fileList.filter(f => f.size > MAX_SIZE);
    if (oversized.length > 0) {
      toast.error(`Some files are too large. Max size is 50MB. (${oversized[0].name})`);
      return;
    }

    setPendingFiles(fileList);
    setShowFileConfirm(true);
  }, []);

  const confirmFileSend = async () => {
    const COMPRESSION_THRESHOLD = 5 * 1024 * 1024; // 5MB
    const needsCompression = pendingFiles.filter(f => f.size > COMPRESSION_THRESHOLD && f.type.startsWith("image/"));

    setShowFileConfirm(false);
    if (needsCompression.length > 0) {
      setShowCompressionModal(true);
      return;
    }
 
    await performUpload(pendingFiles);
    setPendingFiles([]);
  };
 
  const performUpload = async (filesToUpload) => {
    try {
      setIsUploading(true);
      const formData = new FormData();
      const finalFiles = await convertImagesToWebP(filesToUpload);

      finalFiles.forEach(file => {
        formData.append("documents", file);
      });
 
      const res = await chatBulkUploadApi(formData);
     
      if (res?.success && res.files) {
        res.files.forEach(file => {
          const type = file.mimetype?.startsWith("image/") ? "image" : "file";
          onSend(file.url, type);
        });
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload files.");
    } finally {
      setIsUploading(false);
    }
  };
 
  const handleStartCompression = async () => {
    setIsCompressing(true);
    try {
      const compressedFiles = await Promise.all(
        pendingFiles.map(async (file) => {
          if (file.type.startsWith("image/") && file.size > 5 * 1024 * 1024) {
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            };
            try {
              const compressedBlob = await imageCompression(file, options);
              return new File([compressedBlob], file.name, { type: file.type });
            } catch (e) {
              console.error("Compression failed for", file.name, e);
              return file;
            }
          }
          return file;
        })
      );
 
      setShowCompressionModal(false);
      await performUpload(compressedFiles);
    } catch (err) {
      toast.error("Error during compression");
    } finally {
      setIsCompressing(false);
      setPendingFiles([]);
    }
  };
 
  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === "file") {
        files.push(items[i].getAsFile());
      }
    }
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  useEffect(() => {
    const hasDraggedFiles = (event) =>
      Array.from(event.dataTransfer?.types || []).includes("Files");

    const handleDocumentDragEnter = (event) => {
      if (!hasDraggedFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDragging(true);
    };

    const handleDocumentDragOver = (event) => {
      if (!hasDraggedFiles(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    };

    const handleDocumentDragLeave = (event) => {
      if (!hasDraggedFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setIsDragging(false);
    };

    const handleDocumentDrop = (event) => {
      if (!hasDraggedFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      dragDepthRef.current = 0;
      setIsDragging(false);
      handleFiles(event.dataTransfer.files);
    };

    document.addEventListener("dragenter", handleDocumentDragEnter);
    document.addEventListener("dragover", handleDocumentDragOver);
    document.addEventListener("dragleave", handleDocumentDragLeave);
    document.addEventListener("drop", handleDocumentDrop);

    return () => {
      document.removeEventListener("dragenter", handleDocumentDragEnter);
      document.removeEventListener("dragover", handleDocumentDragOver);
      document.removeEventListener("dragleave", handleDocumentDragLeave);
      document.removeEventListener("drop", handleDocumentDrop);
    };
  }, [handleFiles]);
 
  const onEmojiClick = (emojiObject) => {
    setText((prev) => prev + emojiObject.emoji);
  };
 
  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiRef.current && !emojiRef.current.contains(event.target)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
 
  return (
    <div
      className="relative border-t border-white/10 bg-black/10 p-3"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onDragEnter={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
      }}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="pointer-events-none fixed inset-0 z-[999] flex items-center justify-center bg-black/45 p-5 text-white backdrop-blur-sm">
          <div className="flex w-full max-w-sm flex-col items-center rounded-3xl border border-dashed border-violet-300/70 bg-violet-950/80 px-8 py-10 text-center shadow-2xl">
            <UploadCloud className="mb-3 h-10 w-10 text-violet-100" />
            <p className="text-base font-bold">Drop files to attach</p>
            <p className="mt-1 text-xs text-white/65">Release anywhere in this chat. You will confirm before sending.</p>
          </div>
        </div>
      )}
      <div
        className="relative flex flex-1 items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.08] px-3 py-2 shadow-inner shadow-black/10 backdrop-blur-xl transition focus-within:border-violet-300/45 focus-within:bg-white/[0.11] focus-within:ring-4 focus-within:ring-violet-500/10"
        ref={emojiRef}
      >
       
        <button
          onClick={() => setShowEmoji((prev) => !prev)}
          className="flex h-8 w-8 items-center justify-center rounded-xl text-white/55 transition hover:bg-white/10 hover:text-white"
          type="button"
          title="Emoji"
        >
          <Smile size={18} />
        </button>
 
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex h-8 w-8 items-center justify-center rounded-xl text-white/55 transition hover:bg-white/10 hover:text-white disabled:opacity-50"
          type="button"
          disabled={isUploading}
          title="Attach file"
        >
          {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
        </button>
 
        <input
          type="file"
          multiple
          className="hidden"
          ref={fileInputRef}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
 
        {showEmoji && (
          <div className="absolute bottom-full left-0 mb-4 z-50 animate-in zoom-in-95 slide-in-from-bottom-10 duration-200">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-tr from-indigo-500 to-purple-600 rounded-[2rem] blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
             
              <div className="relative bg-[#0f111a]/95 backdrop-blur-3xl border border-white/10 rounded-[1.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden">
                <EmojiPicker
                  onEmojiClick={onEmojiClick}
                  theme="dark"
                  searchDisabled={false}
                  skinTonesDisabled={true}
                  autoFocusSearch={false}
                  width={320}
                  height={400}
                  previewConfig={{ showPreview: false }}
                  className="!bg-transparent !border-none"
                />
              </div>
            </div>
          </div>
        )}
 
        <input
          ref={inputRef}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/35"
          placeholder="Type a message..."
          disabled={isUploading}
        />
 
        {showTagList && members.length > 0 && (
          <div className="absolute bottom-full left-10 mb-2 w-64 bg-[#1a1c23]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden z-[60] animate-in slide-in-from-bottom-2">
            <div className="p-2 border-b border-white/5 bg-white/5">
              <span className="text-[10px] text-white/40 font-bold uppercase tracking-wider">Mention Someone</span>
            </div>
            <div className="max-h-48 overflow-y-auto custom-scrollbar">
              {members
                .filter(m =>
                  getMemberName(m).toLowerCase().includes(tagSearch)
                )
                .map((member, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectTag(member)}
                    className="w-full text-left px-3 py-2 hover:bg-white/10 transition-colors flex items-center gap-3"
                    type="button"
                  >
                    <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-[10px] font-bold text-indigo-400 border border-indigo-500/30">
                      {getMemberName(member).charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white/90">{getMemberName(member)}</p>
                      <p className="text-[10px] text-white/40">@{getMemberName(member).toLowerCase().split(" ")[0]}</p>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        )}
 
        <button
          onClick={handleSend}
          className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-600 text-white shadow-lg shadow-violet-950/25 transition hover:bg-violet-500 disabled:bg-white/10 disabled:text-white/30 disabled:shadow-none"
          disabled={isUploading || !text.trim()}
          title="Send"
        >
          <Send size={18} />
        </button>
      </div>

      {showFileConfirm && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-[#191b24] shadow-[0_20px_60px_rgba(0,0,0,0.55)]">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div>
                <h3 className="text-lg font-bold text-white">Send attachment?</h3>
                <p className="text-xs text-white/45">Review files before they are uploaded.</p>
              </div>
              <button
                onClick={() => {
                  setShowFileConfirm(false);
                  setPendingFiles([]);
                }}
                className="rounded-xl p-2 text-white/55 transition hover:bg-white/10 hover:text-white"
                type="button"
                title="Cancel"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-64 space-y-2 overflow-y-auto p-4">
              {pendingFiles.map((file) => (
                <div key={`${file.name}-${file.size}`} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] p-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-200">
                    <Paperclip size={17} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">{file.name}</p>
                    <p className="text-xs text-white/45">{formatFileSize(file.size)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 border-t border-white/10 p-4">
              <button
                onClick={() => {
                  setShowFileConfirm(false);
                  setPendingFiles([]);
                }}
                className="flex-1 rounded-2xl bg-white/10 py-3 text-sm font-bold text-white/70 transition hover:bg-white/15"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={confirmFileSend}
                className="flex-1 rounded-2xl bg-violet-600 py-3 text-sm font-bold text-white shadow-lg shadow-violet-950/25 transition hover:bg-violet-500"
                type="button"
              >
                Send {pendingFiles.length > 1 ? `${pendingFiles.length} files` : "file"}
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* COMPRESSION MODAL */}
      {showCompressionModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-[#1a1c23] border border-white/10 rounded-[2rem] p-8 max-w-sm w-full shadow-[0_20px_50px_rgba(0,0,0,0.5)] text-center relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 animate-gradient-x" />
           
            <div className="w-20 h-20 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-indigo-500/20 group-hover:scale-110 transition-transform duration-500">
              <Paperclip className="text-indigo-400 animate-pulse" size={32} />
            </div>
 
            <h3 className="text-xl font-bold text-white mb-2">Files are too large</h3>
            <p className="text-sm text-white/50 mb-8 leading-relaxed">
              Some images are over 5MB. Would you like to compress them for a faster and smoother upload?
            </p>
 
            <div className="flex flex-col gap-3">
              <button
                onClick={handleStartCompression}
                disabled={isCompressing}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3.5 rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20 disabled:opacity-50"
              >
                {isCompressing ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Compressing...
                  </>
                ) : (
                  "Compress & Upload"
                )}
              </button>
             
              <button
                onClick={() => {
                  setShowCompressionModal(false);
                  setPendingFiles([]);
                }}
                disabled={isCompressing}
                className="w-full bg-white/5 hover:bg-white/10 text-white/70 font-bold py-3.5 rounded-2xl transition-all active:scale-95 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
 

// import { useState, useRef, useEffect } from "react";
// import { Smile, Send, Paperclip, Loader2 } from "lucide-react";
// import EmojiPicker from "emoji-picker-react";
// import { chatBulkUploadApi } from "../../app/api";
// import toast from "react-hot-toast";
// import imageCompression from "browser-image-compression";

// export default function ChatInput({ onSend, members = [] }) {
//   const [text, setText] = useState("");
//   const [showEmoji, setShowEmoji] = useState(false);
//   const [isUploading, setIsUploading] = useState(false);
//   const [isCompressing, setIsCompressing] = useState(false);
  
//   // Compression Modal State
//   const [showCompressionModal, setShowCompressionModal] = useState(false);
//   const [pendingFiles, setPendingFiles] = useState([]);
  
//   // Tagging State
//   const [showTagList, setShowTagList] = useState(false);
//   const [tagSearch, setTagSearch] = useState("");
//   const [cursorPos, setCursorPos] = useState(0);

//   const fileInputRef = useRef(null);
//   const emojiRef = useRef(null);
//   const inputRef = useRef(null);

//   const handleSend = () => {
//     if (!text.trim()) return;

//     onSend(text);
//     setText("");
//     setShowEmoji(false);
//     setShowTagList(false);
//   };

//   const handleChange = (e) => {
//     const val = e.target.value;
//     const pos = e.target.selectionStart;
//     setText(val);
//     setCursorPos(pos);

//     // Detect @ mention
//     const textBeforeCursor = val.slice(0, pos);
//     const words = textBeforeCursor.split(/\s/);
//     const lastWord = words[words.length - 1];

//     if (lastWord.startsWith("@")) {
//       setShowTagList(true);
//       setTagSearch(lastWord.slice(1).toLowerCase());
//     } else {
//       setShowTagList(false);
//     }
//   };

//   const handleSelectTag = (member) => {
//     const textBeforeCursor = text.slice(0, cursorPos);
//     const textAfterCursor = text.slice(cursorPos);
    
//     const words = textBeforeCursor.split(/\s/);
//     words[words.length - 1] = `@${member.first_name}${member.last_name ? ' ' + member.last_name : ''} `;
    
//     const newText = words.join(" ") + textAfterCursor;
//     setText(newText);
//     setShowTagList(false);
//     inputRef.current?.focus();
//   };

//   const handleKeyDown = (e) => {
//     if (e.key === "Enter" && !e.shiftKey) {
//       e.preventDefault();
//       handleSend();
//     }
//   };

//   const handleFiles = async (files) => {
//     if (!files || files.length === 0) return;
    
//     if (files.length > 10) {
//       toast.error("You can only upload up to 10 files at once.");
//       return;
//     }

//     const MAX_SIZE = 50 * 1024 * 1024; // 50MB
//     const COMPRESSION_THRESHOLD = 5 * 1024 * 1024; // 5MB

//     const oversized = Array.from(files).filter(f => f.size > MAX_SIZE);
//     if (oversized.length > 0) {
//       toast.error(`Some files are too large. Max size is 50MB. (${oversized[0].name})`);
//       return;
//     }

//     const needsCompression = Array.from(files).filter(f => f.size > COMPRESSION_THRESHOLD && f.type.startsWith("image/"));
    
//     if (needsCompression.length > 0) {
//       setPendingFiles(Array.from(files));
//       setShowCompressionModal(true);
//       return;
//     }

//     await performUpload(Array.from(files));
//   };

//   const performUpload = async (filesToUpload) => {
//     try {
//       setIsUploading(true);
//       const formData = new FormData();
//       filesToUpload.forEach(file => {
//         formData.append("documents", file);
//       });

//       const res = await chatBulkUploadApi(formData);
      
//       if (res?.success && res.files) {
//         res.files.forEach(file => {
//           const type = file.mimetype?.startsWith("image/") ? "image" : "file";
//           onSend(file.url, type);
//         });
//       }
//     } catch (err) {
//       console.error("Upload error:", err);
//       toast.error("Failed to upload files.");
//     } finally {
//       setIsUploading(false);
//     }
//   };

//   const handleStartCompression = async () => {
//     setIsCompressing(true);
//     try {
//       const compressedFiles = await Promise.all(
//         pendingFiles.map(async (file) => {
//           if (file.type.startsWith("image/") && file.size > 5 * 1024 * 1024) {
//             const options = {
//               maxSizeMB: 1,
//               maxWidthOrHeight: 1920,
//               useWebWorker: true,
//             };
//             try {
//               const compressedBlob = await imageCompression(file, options);
//               return new File([compressedBlob], file.name, { type: file.type });
//             } catch (e) {
//               console.error("Compression failed for", file.name, e);
//               return file;
//             }
//           }
//           return file;
//         })
//       );

//       setShowCompressionModal(false);
//       await performUpload(compressedFiles);
//     } catch (err) {
//       toast.error("Error during compression");
//     } finally {
//       setIsCompressing(false);
//       setPendingFiles([]);
//     }
//   };

//   const handlePaste = (e) => {
//     const items = e.clipboardData.items;
//     const files = [];
//     for (let i = 0; i < items.length; i++) {
//       if (items[i].kind === "file") {
//         files.push(items[i].getAsFile());
//       }
//     }
//     if (files.length > 0) {
//       handleFiles(files);
//     }
//   };

//   const onEmojiClick = (emojiObject) => {
//     setText((prev) => prev + emojiObject.emoji);
//   };

//   // Close emoji picker when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (emojiRef.current && !emojiRef.current.contains(event.target)) {
//         setShowEmoji(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   return (
//     <div className="relative p-3 border-t border-white/10 flex items-center gap-3">
//       <div className="flex-1 flex gap-2 items-center bg-white/10 backdrop-blur rounded-xl px-3 py-2 relative" ref={emojiRef}>
        
//         <button
//           onClick={() => setShowEmoji((prev) => !prev)}
//           className="opacity-70 hover:opacity-100 text-white p-1"
//           type="button"
//         >
//           <Smile size={20} />
//         </button>

//         <button
//           onClick={() => fileInputRef.current?.click()}
//           className="opacity-70 hover:opacity-100 text-white p-1"
//           type="button"
//           disabled={isUploading}
//         >
//           {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
//         </button>

//         <input 
//           type="file"
//           multiple
//           className="hidden"
//           ref={fileInputRef}
//           onChange={(e) => handleFiles(e.target.files)}
//         />

//         {showEmoji && (
//           <div className="absolute bottom-full left-0 mb-4 z-50 animate-in zoom-in-95 slide-in-from-bottom-10 duration-200">
//             <div className="relative group">
//               <div className="absolute -inset-1 bg-gradient-to-tr from-indigo-500 to-purple-600 rounded-[2rem] blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
              
//               <div className="relative bg-[#0f111a]/95 backdrop-blur-3xl border border-white/10 rounded-[1.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden">
//                 <EmojiPicker
//                   onEmojiClick={onEmojiClick}
//                   theme="dark"
//                   searchDisabled={false}
//                   skinTonesDisabled={true}
//                   autoFocusSearch={false}
//                   width={320}
//                   height={400}
//                   previewConfig={{ showPreview: false }}
//                   className="!bg-transparent !border-none"
//                 />
//               </div>
//             </div>
//           </div>
//         )}

//         <input
//           ref={inputRef}
//           value={text}
//           onChange={handleChange}
//           onKeyDown={handleKeyDown}
//           onPaste={handlePaste}
//           className="flex-1 bg-transparent outline-none text-sm text-white"
//           placeholder="Type a message..."
//           disabled={isUploading}
//         />

//         {showTagList && members.length > 0 && (
//           <div className="absolute bottom-full left-10 mb-2 w-64 bg-[#1a1c23]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden z-[60] animate-in slide-in-from-bottom-2">
//             <div className="p-2 border-b border-white/5 bg-white/5">
//               <span className="text-[10px] text-white/40 font-bold uppercase tracking-wider">Mention Someone</span>
//             </div>
//             <div className="max-h-48 overflow-y-auto custom-scrollbar">
//               {members
//                 .filter(m => 
//                   `${m.first_name} ${m.last_name || ""}`.toLowerCase().includes(tagSearch)
//                 )
//                 .map((member, idx) => (
//                   <button
//                     key={idx}
//                     onClick={() => handleSelectTag(member)}
//                     className="w-full text-left px-3 py-2 hover:bg-white/10 transition-colors flex items-center gap-3"
//                     type="button"
//                   >
//                     <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-[10px] font-bold text-indigo-400 border border-indigo-500/30">
//                       {member.first_name[0]}
//                     </div>
//                     <div>
//                       <p className="text-sm font-medium text-white/90">{member.first_name} {member.last_name || ""}</p>
//                       <p className="text-[10px] text-white/40">@{member.first_name.toLowerCase()}</p>
//                     </div>
//                   </button>
//                 ))}
//             </div>
//           </div>
//         )}

//         <button 
//           onClick={handleSend}
//           className="text-purple-400 hover:text-purple-300 transition-colors p-1 disabled:opacity-50"
//           disabled={isUploading || !text.trim()}
//         >
//           <Send size={20} />
//         </button>
//       </div>

//       {/* COMPRESSION MODAL */}
//       {showCompressionModal && (
//         <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
//           <div className="bg-[#1a1c23] border border-white/10 rounded-[2rem] p-8 max-w-sm w-full shadow-[0_20px_50px_rgba(0,0,0,0.5)] text-center relative overflow-hidden group">
//             <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 animate-gradient-x" />
            
//             <div className="w-20 h-20 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-indigo-500/20 group-hover:scale-110 transition-transform duration-500">
//               <Paperclip className="text-indigo-400 animate-pulse" size={32} />
//             </div>

//             <h3 className="text-xl font-bold text-white mb-2">Files are too large</h3>
//             <p className="text-sm text-white/50 mb-8 leading-relaxed">
//               Some images are over 5MB. Would you like to compress them for a faster and smoother upload?
//             </p>

//             <div className="flex flex-col gap-3">
//               <button
//                 onClick={handleStartCompression}
//                 disabled={isCompressing}
//                 className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3.5 rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20 disabled:opacity-50"
//               >
//                 {isCompressing ? (
//                   <>
//                     <Loader2 size={18} className="animate-spin" />
//                     Compressing...
//                   </>
//                 ) : (
//                   "Compress & Upload"
//                 )}
//               </button>
              
//               <button
//                 onClick={() => {
//                   setShowCompressionModal(false);
//                   setPendingFiles([]);
//                 }}
//                 disabled={isCompressing}
//                 className="w-full bg-white/5 hover:bg-white/10 text-white/70 font-bold py-3.5 rounded-2xl transition-all active:scale-95 disabled:opacity-50"
//               >
//                 Cancel
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }
